import React, { useState } from 'react';
import { MdAccessible, MdFlightTakeoff, MdFlightLand } from 'react-icons/md';
import InfoPopover from './InfoPopover';
import PaxAssistanceSelector from './PaxAssistanceSelector';

const FlightPickerForm = ({ location, onSubmit, submitting, submitError }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [flightNumber, setFlightNumber] = useState('');
    const [flightType, setFlightType] = useState(''); // 'departure' | 'arrival'
    const [selectedAssistance, setSelectedAssistance] = useState([]);
    const [showInfo, setShowInfo] = useState(false);

    const [nameError, setNameError] = useState('');
    const [flightError, setFlightError] = useState('');
    const [dateError, setDateError] = useState('');
    const [typeError, setTypeError] = useState('');

    const today     = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    const tomorrow  = new Date(Date.now() + 86400000).toISOString().split('T')[0];

    // Default to today; passenger can change to yesterday (overnight arrival) or tomorrow (early departure)
    const [flightDate, setFlightDate] = useState(today);

    // Letters (incl. accented), spaces, hyphens, apostrophes — matches backend NAME_REGEX
    const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'\-]+$/;
    // IATA/ICAO: 2–3 char carrier + 1–4 digits + optional suffix letter — matches backend FLIGHT_NUMBER_REGEX
    const FLIGHT_REGEX = /^[A-Z0-9]{2,3}\d{1,4}[A-Z]?$/;

    const handleSubmit = () => {
        const trimFirst  = firstName.trim();
        const trimLast   = lastName.trim();
        const trimFlight = flightNumber.trim().toUpperCase().replace(/\s+/g, '');
        let hasError = false;

        if (!trimFirst || !trimLast) {
            setNameError('Please enter your first and last name.');
            hasError = true;
        } else if (trimFirst.length > 50 || trimLast.length > 50) {
            setNameError('Names must be 50 characters or fewer.');
            hasError = true;
        } else if (!NAME_REGEX.test(trimFirst) || !NAME_REGEX.test(trimLast)) {
            setNameError('Names can only contain letters, spaces, hyphens and apostrophes.');
            hasError = true;
        } else {
            setNameError('');
        }

        if (!trimFlight || !FLIGHT_REGEX.test(trimFlight)) {
            setFlightError('Please enter a valid flight number, e.g. EI159 or BAW1234.');
            hasError = true;
        } else {
            setFlightError('');
        }

        if (!flightType) {
            setTypeError('Please select whether this is a departure or arrival flight.');
            hasError = true;
        } else {
            setTypeError('');
        }

        if (!flightDate) {
            setDateError('Please select the flight date.');
            hasError = true;
        } else {
            setDateError('');
        }

        if (hasError) return;

        onSubmit(trimFirst, trimLast, trimFlight, flightDate, flightType, selectedAssistance);
    };

    return (
        <div>
            {/* Hero */}
            <div className="rc-form-hero">
                <MdAccessible className="rc-form-hero-icon" aria-hidden="true" />
                <p className="rc-title">Request Assistance</p>
                <p className="rc-subtitle">
                    We'll send someone to collect you from <strong>{location.title}</strong>.
                </p>
            </div>

            {/* Name row */}
            <div className="rc-field-row">
                <div className="rc-field-group">
                    <label className="rc-field-label" htmlFor="rc-first-name">
                        First name <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                        id="rc-first-name"
                        type="text"
                        className="rc-input"
                        placeholder="Jane"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        autoComplete="given-name"
                        maxLength={50}
                    />
                </div>
                <div className="rc-field-group">
                    <label className="rc-field-label" htmlFor="rc-last-name">
                        Last name <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                        id="rc-last-name"
                        type="text"
                        className="rc-input"
                        placeholder="Smith"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        autoComplete="family-name"
                        maxLength={50}
                    />
                </div>
            </div>
            {nameError && <div className="rc-error" role="alert">{nameError}</div>}

            <hr className="rc-divider" />

            {/* Flight number */}
            <div className="rc-field-group">
                <label className="rc-field-label" htmlFor="rc-flight-number">
                    Flight number <span style={{ color: '#dc3545' }}>*</span>
                    <button
                        type="button"
                        className="rc-info-btn"
                        aria-label="Where to find your flight number"
                        onClick={() => setShowInfo(true)}
                    >
                        &#9432;
                    </button>
                </label>
                <input
                    id="rc-flight-number"
                    type="text"
                    className="rc-input"
                    placeholder="e.g. EI159"
                    value={flightNumber}
                    onChange={e => setFlightNumber(e.target.value.toUpperCase())}
                    autoComplete="off"
                    autoCapitalize="characters"
                    maxLength={8}
                />
            </div>
            {flightError && <div className="rc-error" role="alert">{flightError}</div>}

            {/* Flight direction */}
            <div className="rc-field-group" style={{ marginTop: 4 }}>
                <label className="rc-field-label">
                    Flight direction <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <div className="rc-direction-toggle">
                    <button
                        type="button"
                        className={`rc-direction-btn${flightType === 'departure' ? ' selected' : ''}`}
                        onClick={() => setFlightType('departure')}
                    >
                        <MdFlightTakeoff aria-hidden="true" />
                        <span>Departure</span>
                    </button>
                    <button
                        type="button"
                        className={`rc-direction-btn${flightType === 'arrival' ? ' selected' : ''}`}
                        onClick={() => setFlightType('arrival')}
                    >
                        <MdFlightLand aria-hidden="true" />
                        <span>Arrival</span>
                    </button>
                </div>
            </div>
            {typeError && <div className="rc-error" role="alert">{typeError}</div>}

            {/* Flight date */}
            <div className="rc-field-group" style={{ marginTop: 12 }}>
                <label className="rc-field-label" htmlFor="rc-flight-date">
                    {flightType === 'arrival' ? 'Landing date' : 'Departure date'}
                    <span style={{ color: '#dc3545' }}> *</span>
                </label>
                <input
                    id="rc-flight-date"
                    type="date"
                    className="rc-input"
                    value={flightDate}
                    min={yesterday}
                    max={tomorrow}
                    onChange={e => setFlightDate(e.target.value)}
                />
            </div>
            {dateError && <div className="rc-error" role="alert">{dateError}</div>}

            <hr className="rc-divider" />

            {/* Assistance requirements */}
            <div className="rc-field-group">
                <label className="rc-field-label">
                    Assistance requirements
                    <span style={{ color: '#6c757d', fontWeight: 400, marginLeft: 5, fontSize: '0.78rem' }}>(optional)</span>
                </label>
                <PaxAssistanceSelector
                    value={selectedAssistance}
                    setAssistance={setSelectedAssistance}
                    iata={location.iata}
                />
            </div>

            {submitError && (
                <div className="rc-error" role="alert">
                    {submitError}
                    <br />
                </div>
            )}

            <button
                type="button"
                className="rc-btn-primary"
                onClick={handleSubmit}
                disabled={submitting}
                aria-busy={submitting}
            >
                {submitting ? 'Submitting…' : 'Submit Request'}
            </button>

            {showInfo && <InfoPopover onClose={() => setShowInfo(false)} />}
        </div>
    );
};

export default FlightPickerForm;
